import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Calendar, Zap, ImageIcon, MessageSquare, ArrowRight, Sparkles, Gift } from "lucide-react";

const to12h = (hhmm) => {
    if (!hhmm || typeof hhmm !== "string") return hhmm;
    const [h, m] = hhmm.split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
    const period = h >= 12 ? "PM" : "AM";
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${m.toString().padStart(2, "0")} ${period}`;
};

export default function Dashboard() {
    const { user } = useAuth();
    const { sub } = useOutletContext();
    const [bookings, setBookings] = useState([]);
    const [designs, setDesigns] = useState([]);

    useEffect(() => {
        api.get("/bookings").then((r) => setBookings(r.data)).catch((err) => console.warn("bookings fetch failed:", err?.message || err));
        api.get("/designs").then((r) => setDesigns(r.data)).catch((err) => console.warn("designs fetch failed:", err?.message || err));
    }, []);

    const upcoming = bookings.filter((b) => b.status !== "completed").slice(0, 3);
    const pct = sub?.days_remaining ? Math.min(100, Math.round((sub.days_remaining / 30) * 100)) : 0;
    const ringR = 70;
    const C = 2 * Math.PI * ringR;
    const dash = (pct / 100) * C;

    return (
        <div className="max-w-[1200px] mx-auto space-y-8">
            {user?.welcome_bonus_eligible && sub?.status !== "active" && (
                <Link
                    to="/welcome"
                    data-testid="dash-welcome-banner"
                    className="block rounded-2xl p-5 border border-gold/40 bg-gradient-to-r from-gold/15 via-rose/10 to-transparent hover:border-gold/70 transition group"
                >
                    <div className="flex items-center gap-4">
                        <div className="w-11 h-11 rounded-full bg-gold/20 border border-gold/40 flex items-center justify-center shrink-0">
                            <Gift className="w-5 h-5 text-gold" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs tracking-[0.25em] uppercase text-gold">Welcome bonus active</p>
                            <p className="font-serif text-xl mt-0.5">Your first month is 50% off — claim it now</p>
                        </div>
                        <span className="text-rose group-hover:translate-x-1 transition hidden sm:inline-block">→</span>
                    </div>
                </Link>
            )}
            {/* Welcome */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <p className="text-xs tracking-[0.3em] uppercase text-rose">Welcome back</p>
                    <h1 className="font-serif text-4xl sm:text-5xl mt-2">
                        Hi {user?.name?.split(" ")[0] || "beautiful"} <span className="italic text-rose">✦</span>
                    </h1>
                </div>
                <div className="flex gap-3">
                    <Link to="/portal/book" data-testid="dash-book-btn" className="px-5 py-2.5 rounded-full bg-rose text-ink hover:bg-roseHover transition font-medium flex items-center gap-2">
                        <Calendar className="w-4 h-4" /> Book Appointment
                    </Link>
                    <Link to="/portal/book?emergency=1" data-testid="dash-emergency-btn" className="px-5 py-2.5 rounded-full border border-rose text-rose hover:bg-rose hover:text-ink transition flex items-center gap-2 animate-pulse-rose">
                        <Zap className="w-4 h-4" /> Emergency · Within the hour
                    </Link>
                </div>
            </div>

            {/* Top widgets */}
            <div className="grid lg:grid-cols-3 gap-6">
                {/* Subscription ring */}
                <div data-testid="sub-ring-card" className="lg:col-span-1 rounded-2xl border border-white/10 bg-surface p-6 flex items-center gap-6">
                    <div className="relative w-44 h-44 shrink-0">
                        <svg viewBox="0 0 160 160" className="w-44 h-44 -rotate-90">
                            <circle cx="80" cy="80" r={ringR} className="stroke-white/8" strokeWidth="10" fill="none" />
                            <circle
                                cx="80"
                                cy="80"
                                r={ringR}
                                className="stroke-rose transition-all duration-1000"
                                strokeWidth="10"
                                fill="none"
                                strokeLinecap="round"
                                strokeDasharray={`${dash} ${C}`}
                            />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="font-serif text-5xl text-rose">{sub?.days_remaining ?? 0}</span>
                            <span className="text-xs text-white/50 mt-1">days left</span>
                        </div>
                    </div>
                    <div>
                        <p className="text-xs tracking-[0.3em] uppercase text-white/50">Subscription</p>
                        <p className="font-serif text-2xl mt-1 capitalize">{sub?.plan || "Not active"}</p>
                        <p className="text-sm text-white/60 mt-2 capitalize">Status: {sub?.status || "inactive"}</p>
                        <Link to="/portal/subscription" data-testid="dash-manage-sub" className="inline-flex mt-4 items-center gap-1 text-rose text-sm hover:underline">
                            Manage <ArrowRight className="w-3 h-3" />
                        </Link>
                    </div>
                </div>

                {/* Upcoming */}
                <div data-testid="upcoming-card" className="lg:col-span-2 rounded-2xl border border-white/10 bg-surface p-6">
                    <div className="flex items-center justify-between">
                        <p className="text-xs tracking-[0.3em] uppercase text-white/50">Upcoming appointments</p>
                        <Link to="/portal/book" className="text-rose text-xs hover:underline">+ New</Link>
                    </div>
                    {upcoming.length === 0 ? (
                        <div className="mt-6 py-8 text-center text-white/50">
                            <Calendar className="w-8 h-8 mx-auto text-rose/60" />
                            <p className="mt-3">No appointments yet — book your first one.</p>
                        </div>
                    ) : (
                        <ul className="mt-4 divide-y divide-white/5">
                            {upcoming.map((b) => (
                                <li key={b.booking_id} className="py-4 flex items-center justify-between">
                                    <div>
                                        <p className="font-serif text-lg capitalize">{b.service.replace(/_/g, " ")}</p>
                                        <p className="text-xs text-white/50 mt-0.5">
                                            {b.date} · {to12h(b.time)} · {b.address}
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        {b.emergency && <span className="text-rose text-xs px-2 py-1 rounded-full bg-rose/10 border border-rose/30">Emergency</span>}
                                        {b.upcharge ? <p className="text-xs text-gold mt-1">+${b.upcharge.toFixed(2)}</p> : null}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {/* Quick links */}
            <div className="grid md:grid-cols-3 gap-6">
                <Link
                    to="/portal/gallery"
                    data-testid="quick-gallery"
                    className="group p-6 rounded-2xl border border-white/10 bg-surface hover:border-rose/40 transition"
                >
                    <ImageIcon className="w-6 h-6 text-rose" />
                    <p className="font-serif text-2xl mt-3">Your gallery</p>
                    <p className="text-sm text-white/60 mt-1">{designs.length} designs saved</p>
                    <p className="text-rose text-xs mt-3 group-hover:underline">Browse & upload →</p>
                </Link>
                <Link
                    to="/portal/messages"
                    data-testid="quick-messages"
                    className="group p-6 rounded-2xl border border-white/10 bg-surface hover:border-rose/40 transition"
                >
                    <MessageSquare className="w-6 h-6 text-rose" />
                    <p className="font-serif text-2xl mt-3">Talk to us</p>
                    <p className="text-sm text-white/60 mt-1">30-min response guarantee</p>
                    <p className="text-rose text-xs mt-3 group-hover:underline">Open chat →</p>
                </Link>
                <Link
                    to="/portal/subscription"
                    data-testid="quick-sub"
                    className="group p-6 rounded-2xl border border-white/10 bg-surface hover:border-rose/40 transition"
                >
                    <Sparkles className="w-6 h-6 text-rose" />
                    <p className="font-serif text-2xl mt-3">Renew or upgrade</p>
                    <p className="text-sm text-white/60 mt-1">$55 Glow · $75 Acrylic</p>
                    <p className="text-rose text-xs mt-3 group-hover:underline">Manage plan →</p>
                </Link>
            </div>
        </div>
    );
}
